import { reportMessage } from '../observability';
import type { ObservabilityContextValue, ObservabilitySeverity } from '../observability';
import { z } from 'zod';

const observabilityContextValueSchema: z.ZodType<ObservabilityContextValue> = z.lazy(() =>
  z.union([
    z.boolean(),
    z.null(),
    z.number(),
    z.string(),
    z.array(observabilityContextValueSchema),
    z.record(z.string(), observabilityContextValueSchema),
  ])
);

const serializeExternalContext = <ExternalContext>(context: ExternalContext): ObservabilityContextValue | null => {
  try {
    const serialized = JSON.stringify(context, (_key, value) => {
      if (value instanceof Error) {
        return { ...value, name: value.name, message: value.message, stack: value.stack };
      }
      return value;
    });
    if (!serialized) {
      return null;
    }
    const parsedContext = observabilityContextValueSchema.safeParse(JSON.parse(serialized));
    return parsedContext.success ? parsedContext.data : null;
  } catch {
    return null;
  }
};

/**
 * Logs a message to the console and reports it through the vendor-neutral
 * observability facade (fans out to every registered sink). Signature preserved
 * for existing call sites.
 */
export const logMessage = <ExternalContext = ObservabilityContextValue>(
  message: string,
  level: ObservabilitySeverity,
  extra: ExternalContext | null = null
) => {
  console.log(level, message, extra);
  const serializedExtra = extra === null ? null : serializeExternalContext(extra);
  reportMessage(message, level, serializedExtra === null ? undefined : { extra: serializedExtra });
};
